import { useState, useRef } from 'react';
// @ts-ignore-start
import InputMask from "react-input-mask";
// @ts-ignore-end
import { Typography, IconButton, InputAdornment } from '@mui/material';

import { BoxInput, InputCustom, OutlinedInputCustom } from './styles';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

type ParamsInput = {
    name: string;
    type: string;
    label?: string;
    placeholder: string;
    error?: any;
    mask?: boolean;
    identity?: boolean;
    disabled?: boolean;
    textError?: string;
    typeIdentity?: string;
    maskChar?: string;
    maskHolder?: string;
    uppercase?: boolean;
    register?: {
        onChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): unknown;
        ref: React.Ref<HTMLInputElement>;
    };
}

const Input = ({ label, placeholder, type, disabled = false, register, error, textError, mask, identity, typeIdentity, maskChar, maskHolder, name, uppercase = false }: ParamsInput) => {
    const [showPassword, setShowPassword] = useState(false);
    const [maskIdentity, setMaskIdentity] = useState('999.999.999-99');
    const [maskPlaqueChassi, setMaskPlaqueChassi] = useState('*aa9*99');

    const isUserTypingRef = useRef(false);
    const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleTypingStart = () => {
        isUserTypingRef.current = true;
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => { isUserTypingRef.current = false; }, 300);
    };

    const formatRawAsCNPJ = (raw: string): string => {
        const r = raw.toUpperCase().padEnd(14, '_');
        return `${r.slice(0, 2)}.${r.slice(2, 5)}.${r.slice(5, 8)}/${r.slice(8, 12)}-${r.slice(12, 14)}`;
    };

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handlerbeforeMasked = (newState: { value: string; selection: object; }, oldState: { value: any; }, userInput: string) => {
        const { value } = oldState;
        const rawAlphanumeric = value?.replace(/[^0-9A-Z]/gi, '').toUpperCase() ?? '';
        const rawDigitsOnly = value?.replace(/[^0-9]/g, '') ?? '';
        let newValue = newState.value;
        let newSelection = newState.selection;

        if (userInput) {
            const rawUserInput = userInput.replace(/[^0-9A-Z]/gi, '').toUpperCase();
            const combinedRaw = (rawAlphanumeric + rawUserInput).slice(0, 14);
            const isExpandingToCNPJ = combinedRaw.length > 11 || /[A-Z]/.test(combinedRaw);
            if (isExpandingToCNPJ) {
                if (maskIdentity === '999.999.999-99') {
                    newValue = formatRawAsCNPJ(combinedRaw);
                    newSelection = { end: newValue.length, start: newValue.length };
                }
                setMaskIdentity('**.***.***/****-99');
            }
        } else if (!isUserTypingRef.current) {
            if (rawAlphanumeric.length > 11 || /[A-Z]/.test(rawAlphanumeric)) {
                setMaskIdentity('**.***.***/****-99');
            } else {
                setMaskIdentity('999.999.999-99');
                if (rawDigitsOnly.length === 11) newSelection = { end: 14, start: 14 };
            }
        }

        return { value: newValue, selection: newSelection };
    }

    const handleMoveCursorEnd = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();

        const aux = e.target.value;

        e.target.value = '';
        setTimeout(() => {
            e.target.value = aux;
        }, 10)
    }

    const handlerbeforeMaskedPlaque = (newState: { value: string; selection: object; }, oldState: { value: any; }, userInput: string) => {
        let { value } = oldState;
        value = value?.replace(/[^a-zA-Z0-9]/g, '');

        let newValue = newState.value;
        let newSelection = newState.selection;

        if (userInput && userInput.replace(/[^a-zA-Z0-9]/g, '').length > 7) {
            if (userInput.replace(/[^a-zA-Z0-9]/g, '').length === 8) {
                setMaskPlaqueChassi("********");
                newValue = userInput.replace(/[^a-zA-Z0-9]/g, '');
                newSelection = { end: 8, start: 8 };
            } else {
                setMaskPlaqueChassi("*************9999");
                newValue = userInput.replace(/[^a-zA-Z0-9]/g, '');
                newSelection = { end: 17, start: 17 };
            }
        } else if (Number(value[0])) {
            setMaskPlaqueChassi("*************9999");
        } else {
            if ((value.length === 7) && userInput) {
                setMaskPlaqueChassi("*************9999");
                newValue = `${newValue}${userInput}_________`;
                newSelection = { end: 8, start: 8 };
            } else if (value.length <= 7) {
                setMaskPlaqueChassi("*aa9*99");
            }
        }

        return { value: newValue, selection: newSelection };
    }

    const handleValidDocument = (value: string) => {
        const normalized = value?.toUpperCase() ?? '';
        const onlyDigits = normalized.replace(/[^0-9]/g, '');
        const alphanumeric = normalized.replace(/[^0-9A-Z]/g, '');

        const isCnpj = /[A-Z]/.test(alphanumeric) || alphanumeric.length > 11;

        if (isCnpj) {
            const cnpj = alphanumeric.substr(0, 14);
            return cnpj.replace(/(..)(...)(...)(....)(..)/, '$1.$2.$3/$4-$5');
        } else {
            const cpf = onlyDigits.substr(0, 11);
            return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d)/, '$1.$2.$3-$4');
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const maskedValue = handleValidDocument(e.target.value);

        register?.onChange({
            target: {
                name: name,
                value: typeIdentity === 'plaque' ? e.target.value : maskedValue,
            },
        } as unknown as React.ChangeEvent<HTMLInputElement>);
    };

    return (
        <BoxInput id='box-input'>
            {label &&
                <Typography variant="caption" margin={0} paddingBottom={1}>
                    {label}
                </Typography>
            }
            {type === 'password' ?
                <OutlinedInputCustom
                    name={name}
                    type={showPassword ? 'text' : 'password'}
                    placeholder={placeholder}
                    disabled={disabled}
                    color={error && 'error'}
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                            >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    }
                    {...register}
                />
                : mask ?
                    identity ?
                        <InputMask
                            mask={typeIdentity === 'plaque' ? maskPlaqueChassi : maskIdentity}
                            beforeMaskedValueChange={typeIdentity === 'plaque' ? handlerbeforeMaskedPlaque : handlerbeforeMasked}
                            onFocus={(e: React.ChangeEvent<HTMLInputElement>) => handleMoveCursorEnd(e)}
                            onKeyDown={typeIdentity !== 'plaque' ? handleTypingStart : undefined}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}>
                            {() =>
                                <InputCustom
                                    name={name}
                                    type={type}
                                    variant="outlined"
                                    placeholder={placeholder}
                                    margin="none"
                                    error={error ? true : false}
                                    sx={{ 'input': { textTransform: uppercase ? 'uppercase' : 'none' } }}
                                    inputRef={register?.ref}
                                />
                            }
                        </InputMask>
                        :
                        <InputMask
                            disabled={disabled || false}
                            maskChar={maskChar}
                            mask={maskHolder?.toLowerCase() || placeholder.toLowerCase()}
                            onFocus={(e: React.ChangeEvent<HTMLInputElement>) => handleMoveCursorEnd(e)}
                            {...register}
                        >
                            {() =>
                                <InputCustom
                                    name={name}
                                    disabled={disabled || false}
                                    type={type}
                                    variant="outlined"
                                    placeholder={placeholder}
                                    margin="none"
                                    error={error ? true : false}
                                    sx={{ 'input': { textTransform: uppercase ? 'uppercase' : 'none' } }}
                                    inputRef={register?.ref}
                                />
                            }
                        </InputMask>
                    :
                    <InputCustom
                        name={name}
                        type={type}
                        variant="outlined"
                        placeholder={placeholder}
                        margin="none"
                        disabled={disabled}
                        error={error ? true : false}
                        sx={{ textTransform: uppercase ? 'uppercase' : 'none' }}
                        inputRef={register?.ref}
                        onChange={(e) => register?.onChange(e)}
                    />
            }
            {error?.type === 'pattern' && <Typography variant='overline'>{textError}</Typography>}
        </BoxInput>
    );
}

export default Input;